import ResizableWrapper from '@/components/common/ResizableWrapper';
import EditSubroutineDialog from '@/components/navigation/menu/EditSubroutineDialog';
import HandleFoldersDialog from '@/components/navigation/menu/HandleFoldersDialog';
import PromptsContextMenuWrapper from '@/components/navigation/menu/PromptsContextMenuWrapper';
import SubroutineContextMenuWrapper from '@/components/navigation/menu/SubroutineContextMenuWrapper';
import SubroutineSortArrow from '@/components/navigation/menu/SubroutineSortArrow';
import AddPromptsToGroupsDialog from '@/components/prompt/AddPromptsToGroupDialog';
import EditPromptGroupsDialog from '@/components/prompt/EditPromptGroupsDialog';
import NewPromptGroupDialog from '@/components/prompt/NewPromptGroupDialog';
import PromptDialog from '@/components/prompt/PromptDialog';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useSubroutine } from '@/hooks/useSubroutine';
import { SearchBlocksItem } from '@/types/search';
import { OpenEditType } from '@/types/subroutine';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { SimpleTreeView } from '@mui/x-tree-view';
import { FC, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import FindReferencesDialog from '../navigation/FindReferences';
import SubroutineTreeItems from './SubroutineTreeItems';
import TreePrompts from './TreePrompts';
import TreeSynchronization from './TreeSynchronization';
import TreeVariables from './TreeVariables';

export type SelectedItem = {
  id?: number;
  name?: string;
  folderId?: number;
  type?: 'subroutine' | 'folder';
};

type TreeMenuProps = {
  openEdit: OpenEditType;
  setOpenEdit: (openEdit: OpenEditType) => void;
};

const TreeMenu: FC<TreeMenuProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { selectedSubroutineId, setSelectedSubroutineId, sortSubroutinesBy, setSortSubroutinesBy } = useSubroutine();

  const [openHandleFoldersDialog, setOpenHandleFoldersDialog] = useState(false);
  const [promptGroupId, setPromptGroupId] = useState(0);
  const [openPromptDialog, setOpenPromptDialog] = useState(false);
  const [openNewPromptsDialog, setOpenNewPromptsDialog] = useState(false);
  const [openAddPromptsDialog, setOpenAddPromptsDialog] = useState(false);
  const [openEditPromptsDialog, setOpenEditPromptsDialog] = useState(false);
  const [searchBlocksItem, setSearchBlocksItem] = useState<SearchBlocksItem | undefined>();
  const [selectedItem, setSelectedItem] = useState<SelectedItem>();
  const tree = [
    'configuration',
    ['prompts'],
    ['synchronization'],
    ['variables'],
    ['promptlists'],
    ['signals'],
    ['sync.event'],
    ['subroutines']
  ];
  const [expanded, setExpanded] = useState<string[]>(tree.flat().flat());
  const [selectedTreeMenuItem, setSelectedTreeMenuItem] = useState('');

  const { confirm } = useDialogs();
  const theme = useTheme();
  const baseWidth = parseInt(theme.spacing(30), 10);
  const [initialWidth, setInitialWidth] = useLocalStorage('TreeMenuWidth', baseWidth);
  const maxWidth = document.body.clientWidth / 2 - parseInt(theme.spacing(2), 10);

  const deleteFolder = (id: number) => {
    if (!application.folders) return;

    const childSubroutines = application.subroutines.find((subroutine) => subroutine.folder === id);
    const childFolders = application.folders.find((folder) => folder.parentId === id);
    const folderName = application.folders.find((folder) => folder.id === id)?.label;
    if (!!childSubroutines || !!childFolders) {
      confirm({
        title: `Cannot delete folder: ${folderName}`,
        description: `The folder is not empty.`,
        confirmationText: 'Ok',
        hideCancelButton: true
      });
    } else {
      confirm({
        title: `Deleting folder: ${folderName}`,
        description: `Are you sure you want to remove the following folder: ${folderName}?`,
        confirmationText: 'Remove',
        cancellationButtonProps: { color: 'secondary' }
      }).then((result) => {
        if (!result.confirmed) return;

        const folders = application.folders?.filter((folder) => folder.id !== id);
        setApplication({ ...application, folders: folders });
      });
    }
  };

  const getFolderIds = (folderId: number, folders: string[] = []): string[] => {
    folders.push(`folders.${folderId}`);
    const currentFolder = application.folders?.find((folder) => folder.id === folderId);
    return currentFolder?.parentId ? getFolderIds(currentFolder?.parentId, folders) : folders;
  };

  useEffect(() => {
    const currentSubroutine = application.subroutines.find((subroutine) => subroutine.id === selectedSubroutineId);
    if (currentSubroutine?.folder) {
      const newExpanded = getFolderIds(currentSubroutine.folder);
      setExpanded((oldExpanded) => [...oldExpanded, ...newExpanded]);
    }

    setSelectedTreeMenuItem(`flow.${selectedSubroutineId}`);
  }, [selectedSubroutineId]);

  return (
    <>
      <ResizableWrapper
        initialWidth={initialWidth}
        handlePosition='right'
        onResize={(newWidth) => setInitialWidth(newWidth)}
        minWidth={baseWidth}
        maxWidth={maxWidth}
      >
        <SimpleTreeView
          aria-label='block engine builder navigator'
          slots={{ collapseIcon: ExpandMore, expandIcon: ChevronRight }}
          sx={{
            minWidth: baseWidth,
            overflowY: 'auto',
            overflowX: 'hidden',
            height: 'inherit',
            p: 1,
            mr: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            userSelect: 'none',
            color: 'text.primary'
          }}
          selectedItems={selectedTreeMenuItem}
          onItemSelectionToggle={(_, id: string) => setSelectedTreeMenuItem(id)}
          onExpandedItemsChange={(_, ids) => setExpanded(ids)}
          expandedItems={expanded}
        >
          <Typography variant='h5' color='text.primary' pl={1}>
            Configuration
          </Typography>
          <Divider />
          <Box sx={{ display: 'flex' }}>
            <PromptsContextMenuWrapper
              openNewDialog={() => setOpenNewPromptsDialog(true)}
              openAddDialog={(id) => {
                setPromptGroupId(id);
                setOpenAddPromptsDialog(true);
              }}
              openEditDialog={(id) => {
                setPromptGroupId(id);
                setOpenEditPromptsDialog(true);
              }}
            >
              <TreePrompts
                openPromptsDialog={(id) => {
                  setPromptGroupId(id);
                  setOpenPromptDialog(true);
                }}
                openNewDialog={() => setOpenNewPromptsDialog(true)}
                openAddDialog={(id) => {
                  setPromptGroupId(id);
                  setOpenAddPromptsDialog(true);
                }}
                openEditDialog={(id) => {
                  setPromptGroupId(id);
                  setOpenEditPromptsDialog(true);
                }}
              />
            </PromptsContextMenuWrapper>
          </Box>
          <TreeSynchronization />
          <TreeVariables onVariableSearch={(searchValue) => setSearchBlocksItem(searchValue)} />
          <Box sx={{ display: 'flex' }}>
            <SubroutineContextMenuWrapper
              isRoot
              openEdit={(open) => props.setOpenEdit(open)}
              openHandleFoldersDialog={() => setOpenHandleFoldersDialog(true)}
              handleOnSort={(sortBy) => setSortSubroutinesBy(sortBy)}
              setSelectedTreeMenuItem={(newId) => {
                setSelectedTreeMenuItem(newId);
                setExpanded([...expanded, newId]);
              }}
            >
              <SubroutineTreeItems
                itemKey='flow'
                onClick={(subroutineId) => setSelectedSubroutineId(subroutineId)}
                wrapper={SubroutineContextMenuWrapper}
                validate
                deleteFolder={(folderId) => deleteFolder(folderId)}
                openEdit={(open) => props.setOpenEdit(open)}
                openSearch={(searchValue) => setSearchBlocksItem(searchValue)}
                openHandleFoldersDialog={(selectedItem) => {
                  setSelectedItem(selectedItem);
                  setOpenHandleFoldersDialog(true);
                }}
                sortSubroutinesBy={sortSubroutinesBy}
              />
            </SubroutineContextMenuWrapper>
            <SubroutineSortArrow
              sortSubroutinesBy={sortSubroutinesBy}
              onClose={(sortBy) => setSortSubroutinesBy(sortBy)}
            />
          </Box>
        </SimpleTreeView>
      </ResizableWrapper>
      <PromptDialog
        open={openPromptDialog}
        promptGroupId={promptGroupId}
        onPromptSearch={(searchValue) => setSearchBlocksItem(searchValue)}
        onClose={() => setOpenPromptDialog(false)}
      />
      <NewPromptGroupDialog
        open={openNewPromptsDialog}
        selectedSubroutineId={selectedSubroutineId}
        application={application}
        setApplication={setApplication}
        onClose={() => setOpenNewPromptsDialog(false)}
      />
      <AddPromptsToGroupsDialog
        open={openAddPromptsDialog}
        promptGroupId={promptGroupId}
        onClose={() => setOpenAddPromptsDialog(false)}
      />
      <EditPromptGroupsDialog
        open={openEditPromptsDialog}
        promptGroup={application.promptGroups?.find((group) => group.id === promptGroupId)}
        application={application}
        setApplication={setApplication}
        onClose={() => setOpenEditPromptsDialog(false)}
      />
      <EditSubroutineDialog
        open={props.openEdit.open}
        onClose={() => props.setOpenEdit({ open: false })}
        subroutineId={props.openEdit.id}
        setSelectedSubroutineId={(newId) => setSelectedSubroutineId(newId)}
        folderId={props.openEdit.folder}
        type={props.openEdit.type}
      />
      <HandleFoldersDialog
        open={openHandleFoldersDialog}
        selectedItem={selectedItem}
        folders={application.folders}
        application={application}
        setApplication={setApplication}
        onClose={() => {
          setSelectedItem(undefined);
          setOpenHandleFoldersDialog(false);
        }}
      />
      <FindReferencesDialog searchItem={searchBlocksItem} onClose={() => setSearchBlocksItem(undefined)} />
    </>
  );
};

export default TreeMenu;
